import Phaser from 'phaser';
import { TILE_WIDTH, TILE_HEIGHT } from '../utils/IsometricUtils';
import { EventBus } from '../utils/EventBus';

/**
 * Spawn point from Tiled object layer
 */
export interface SpawnPoint {
  name: string;
  type: string;
  x: number;
  y: number;
  properties: Record<string, unknown>;
}

/**
 * Trigger zone from Tiled object layer
 */
export interface TriggerZone {
  name: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  properties: Record<string, unknown>;
}

/**
 * Tileset configuration
 */
export interface TilesetConfig {
  /** Name of the tileset in Tiled */
  name: string;
  /** Phaser texture key for the tileset image */
  key: string;
}

/**
 * TilemapManager - Handles loading and managing Tiled tilemaps
 * Supports isometric maps with multiple layers
 */
export class TilemapManager {
  private scene: Phaser.Scene;
  private tilemap: Phaser.Tilemaps.Tilemap | null = null;
  private layers: Map<string, Phaser.Tilemaps.TilemapLayer> = new Map();
  private collisionLayer: Phaser.Tilemaps.TilemapLayer | null = null;
  private spawnPoints: SpawnPoint[] = [];
  private triggerZones: TriggerZone[] = [];

  /** Map offset for centering */
  private offsetX: number = 0;
  private offsetY: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Load a tilemap from Tiled JSON
   * @param mapKey - The key used when loading the tilemap JSON
   * @param tilesets - Array of tileset configurations
   */
  public loadMap(mapKey: string, tilesets: TilesetConfig[]): void {
    // Create tilemap from loaded JSON
    this.tilemap = this.scene.make.tilemap({ key: mapKey });

    if (!this.tilemap) {
      console.error(`Failed to create tilemap from key: ${mapKey}`);
      return;
    }

    // Add tilesets
    const addedTilesets: Phaser.Tilemaps.Tileset[] = [];
    for (const config of tilesets) {
      const tileset = this.tilemap.addTilesetImage(config.name, config.key);
      if (tileset) {
        addedTilesets.push(tileset);
      } else {
        console.warn(`Failed to add tileset: ${config.name}`);
      }
    }

    if (addedTilesets.length === 0) {
      console.error('No tilesets were added to the tilemap');
      return;
    }

    // Calculate offset to center map
    this.calculateOffset();

    // Create layers
    this.createLayers(addedTilesets);

    // Extract raw collision data from the tilemap
    this.extractCollisionData();

    // Parse object layers
    this.parseObjectLayers();

    // Emit events for trigger zones (shrines, portals, etc.)
    this.emitTriggerZoneEvents();

    console.log(`Tilemap loaded: ${mapKey}`, {
      width: this.tilemap.width,
      height: this.tilemap.height,
      layers: this.layers.size,
      spawnPoints: this.spawnPoints.length,
      triggerZones: this.triggerZones.length,
    });
  }

  /**
   * Emit events for registered trigger zones
   */
  private emitTriggerZoneEvents(): void {
    for (const zone of this.triggerZones) {
      EventBus.emitEvent('trigger:register', {
        name: zone.name,
        type: zone.type,
        bounds: {
          x: zone.x,
          y: zone.y,
          width: zone.width,
          height: zone.height,
        },
        properties: zone.properties,
      });
    }
  }

  /**
   * Calculate offset to center the map on screen
   */
  private calculateOffset(): void {
    if (!this.tilemap) return;

    const camera = this.scene.cameras.main;

    // Center the map horizontally on screen
    this.offsetX = camera.width / 2;
    this.offsetY = 100; // Top padding
  }

  /**
   * Create tilemap layers from the Tiled data
   */
  private createLayers(tilesets: Phaser.Tilemaps.Tileset[]): void {
    if (!this.tilemap) return;

    // Standard layer names we look for
    const layerOrder = ['ground', 'ground2', 'paths', 'decoration', 'decoration2', 'objects', 'collision'];

    for (const layerName of layerOrder) {
      const tiledLayer = this.tilemap.getLayer(layerName);
      if (!tiledLayer) continue;

      const layer = this.tilemap.createLayer(layerName, tilesets, this.offsetX, this.offsetY);

      if (layer) {
        this.layers.set(layerName, layer);

        // Set depth based on layer order
        const depth = layerOrder.indexOf(layerName) * 10 - 100;
        layer.setDepth(depth);

        // Handle collision layer
        if (layerName === 'collision') {
          this.collisionLayer = layer;
          // Make collision layer invisible but keep it for checks
          layer.setVisible(false);
          // Set collision for all non-empty tiles
          layer.setCollisionByExclusion([-1, 0]);
        }

        console.log(`Created layer: ${layerName} at depth ${depth}`);
      }
    }
  }

  /**
   * Extract raw collision data from the tilemap
   */
  private extractCollisionData(): void {
    if (!this.tilemap) {
      console.warn('No tilemap for collision extraction');
      return;
    }

    // Try to get the collision layer data using the stored layer
    if (this.collisionLayer && this.collisionLayer.layer) {
      const layerData = this.collisionLayer.layer;
      // Flatten the 2D array to 1D for easy access
      this.collisionData = [];
      for (let y = 0; y < layerData.height; y++) {
        for (let x = 0; x < layerData.width; x++) {
          const tile = layerData.data[y][x];
          // Store the tile index (0 or -1 = empty/walkable, >0 = collision)
          this.collisionData.push(tile ? tile.index : 0);
        }
      }
      console.log(`Collision data extracted: ${this.collisionData.length} tiles (map: ${this.tilemap.width}x${this.tilemap.height})`);
      // Debug: show values at corners and center
      console.log(`Collision corners: (0,0)=${this.collisionData[0]}, (19,0)=${this.collisionData[19]}, (0,19)=${this.collisionData[380]}, (19,19)=${this.collisionData[399]}`);
      console.log(`Collision interior: (1,1)=${this.collisionData[21]}, (10,10)=${this.collisionData[210]}`);
    } else {
      console.warn('No collision layer found, using bounds only');
    }
  }

  /**
   * Parse object layers for spawn points and triggers
   */
  private parseObjectLayers(): void {
    if (!this.tilemap) return;

    // Look for object layers
    const objectLayerNames = ['spawns', 'triggers', 'objects', 'npcs', 'zones'];

    for (const layerName of objectLayerNames) {
      const objectLayer = this.tilemap.getObjectLayer(layerName);
      if (!objectLayer) continue;

      for (const obj of objectLayer.objects) {
        // Convert Tiled coordinates to world coordinates
        const worldX = this.tiledToWorldX(obj.x ?? 0, obj.y ?? 0);
        const worldY = this.tiledToWorldY(obj.x ?? 0, obj.y ?? 0);

        // Parse custom properties
        const properties: Record<string, unknown> = {};
        if (obj.properties) {
          for (const prop of obj.properties as Array<{ name: string; value: unknown }>) {
            properties[prop.name] = prop.value;
          }
        }

        // Categorize by type
        const objType = (obj.type || obj.name || '').toLowerCase();

        // Check for shrine/trigger types first (before layer name check)
        if (objType.includes('shrine') || objType.includes('trigger') || objType.includes('zone')) {
          console.log(`Trigger zone '${obj.name}' (${obj.type}): Tiled(${obj.x}, ${obj.y}) -> World(${worldX.toFixed(2)}, ${worldY.toFixed(2)})`);
          this.triggerZones.push({
            name: obj.name || 'unnamed',
            type: obj.type || 'trigger',
            x: worldX,
            y: worldY,
            width: (obj.width ?? TILE_WIDTH) / TILE_WIDTH,
            height: (obj.height ?? TILE_HEIGHT) / TILE_HEIGHT,
            properties,
          });
        } else if (objType.includes('spawn') || layerName === 'spawns') {
          console.log(`Spawn point '${obj.name}': Tiled(${obj.x}, ${obj.y}) -> World(${worldX.toFixed(2)}, ${worldY.toFixed(2)})`);
          this.spawnPoints.push({
            name: obj.name || 'unnamed',
            type: obj.type || 'spawn',
            x: worldX,
            y: worldY,
            properties,
          });
        } else if (obj.width) {
          this.triggerZones.push({
            name: obj.name || 'unnamed',
            type: obj.type || 'trigger',
            x: worldX,
            y: worldY,
            width: (obj.width ?? TILE_WIDTH) / TILE_WIDTH,
            height: (obj.height ?? TILE_HEIGHT) / TILE_HEIGHT,
            properties,
          });
        } else {
          // Default to spawn point
          this.spawnPoints.push({
            name: obj.name || 'unnamed',
            type: obj.type || 'object',
            x: worldX,
            y: worldY,
            properties,
          });
        }
      }
    }
  }

  /**
   * Convert Tiled pixel coordinates to world grid coordinates
   * For Tiled isometric maps, object layer coordinates are in pixel space
   * where x increases right and y increases down.
   */
  private tiledToWorld(pixelX: number, pixelY: number): { x: number; y: number } {
    // For Tiled isometric: convert pixel coords to tile coords
    // Formula: tileX = (px / halfWidth + py / halfHeight) / 2
    //          tileY = (py / halfHeight - px / halfWidth) / 2
    const halfWidth = TILE_WIDTH / 2;
    const halfHeight = TILE_HEIGHT / 2;

    return {
      x: (pixelX / halfWidth + pixelY / halfHeight) / 2,
      y: (pixelY / halfHeight - pixelX / halfWidth) / 2,
    };
  }

  /**
   * Convert Tiled pixel X to world grid X
   * @deprecated Use tiledToWorld instead
   */
  private tiledToWorldX(pixelX: number, pixelY: number): number {
    return this.tiledToWorld(pixelX, pixelY).x;
  }

  /**
   * Convert Tiled pixel Y to world grid Y
   * @deprecated Use tiledToWorld instead
   */
  private tiledToWorldY(pixelX: number, pixelY: number): number {
    return this.tiledToWorld(pixelX, pixelY).y;
  }

  /** Raw collision data from tilemap JSON */
  private collisionData: number[] = [];

  /** Debug flag - set to true to log collision checks */
  private debugCollision = false;

  /**
   * Enable/disable collision debug logging
   */
  public setDebugCollision(enabled: boolean): void {
    this.debugCollision = enabled;
  }

  /** Collision layer tile IDs */
  private static readonly COLLISION_BLOCK = 1;    // Blocks movement
  private static readonly COLLISION_TRIGGER = 4;  // Triggers events but allows movement

  /** Track which trigger tiles the player is currently on */
  private activeTriggerTiles: Set<string> = new Set();

  /** Collision radius for entity hitbox */
  private static readonly COLLISION_RADIUS = 0.15;

  /**
   * Check if a world position is walkable (no collision)
   * Border tiles block, collision tiles allow movement but trigger events
   * Uses a collision radius to check multiple points around the position
   */
  public isWalkable(worldX: number, worldY: number): boolean {
    if (!this.tilemap) return true;

    const radius = TilemapManager.COLLISION_RADIUS;

    // Check center and all corners of the collision box
    const pointsToCheck = [
      { x: worldX, y: worldY },                   // center
      { x: worldX - radius, y: worldY - radius }, // top-left
      { x: worldX + radius, y: worldY - radius }, // top-right
      { x: worldX - radius, y: worldY + radius }, // bottom-left
      { x: worldX + radius, y: worldY + radius }, // bottom-right
      { x: worldX - radius, y: worldY },          // left
      { x: worldX + radius, y: worldY },          // right
      { x: worldX, y: worldY - radius },          // top
      { x: worldX, y: worldY + radius },          // bottom
    ];

    for (const point of pointsToCheck) {
      if (!this.isPointWalkable(point.x, point.y)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if a single point is walkable
   * Uses proper isometric point-in-tile testing
   * Checks COLLISION LAYER for blocking - visual tiles don't affect movement
   */
  private isPointWalkable(worldX: number, worldY: number): boolean {
    if (!this.tilemap) return true;

    // Use collision layer for blocking checks (separation of visuals and collision)
    const collisionLayer = this.collisionLayer || this.layers.get('collision');
    if (!collisionLayer) return true;

    // Get the screen position of this point (matching how entities are rendered)
    const screenPos = collisionLayer.tileToWorldXY(worldX, worldY);
    if (!screenPos) return false;

    // Add centering offset to match entity rendering
    const pointScreenX = screenPos.x + TILE_WIDTH / 2;
    const pointScreenY = screenPos.y + TILE_HEIGHT / 2;

    // Check the 4 tiles that could potentially contain this screen point
    const baseTileX = Math.floor(worldX);
    const baseTileY = Math.floor(worldY);

    const tilesToCheck = [
      { x: baseTileX, y: baseTileY },
      { x: baseTileX + 1, y: baseTileY },
      { x: baseTileX, y: baseTileY + 1 },
      { x: baseTileX + 1, y: baseTileY + 1 },
    ];

    for (const { x: tileX, y: tileY } of tilesToCheck) {
      // Skip if out of bounds
      if (tileX < 0 || tileY < 0 || tileX >= this.tilemap.width || tileY >= this.tilemap.height) {
        continue;
      }

      // Check if the screen point is inside this tile's diamond
      if (!this.isPointInTileDiamond(pointScreenX, pointScreenY, tileX, tileY, collisionLayer)) {
        continue;
      }

      // Check collision layer for blocking tiles
      const tile = collisionLayer.getTileAt(tileX, tileY);
      if (tile && tile.index === TilemapManager.COLLISION_BLOCK) {
        if (this.debugCollision) {
          console.log(`BLOCKED: collision tile at (${tileX},${tileY})`);
        }
        return false;
      }
    }

    return true;
  }

  /**
   * Check if a screen point is inside a tile's isometric diamond
   */
  private isPointInTileDiamond(
    screenX: number,
    screenY: number,
    tileX: number,
    tileY: number,
    layer: Phaser.Tilemaps.TilemapLayer
  ): boolean {
    const tilePos = layer.tileToWorldXY(tileX, tileY);
    if (!tilePos) return false;

    // Diamond vertices (same as drawTileOutline in RenderSystem)
    const top = { x: tilePos.x + TILE_WIDTH / 2, y: tilePos.y };
    const right = { x: tilePos.x + TILE_WIDTH, y: tilePos.y + TILE_HEIGHT / 2 };
    const bottom = { x: tilePos.x + TILE_WIDTH / 2, y: tilePos.y + TILE_HEIGHT };
    const left = { x: tilePos.x, y: tilePos.y + TILE_HEIGHT / 2 };

    // Check if point is inside the diamond using cross product signs
    // Point is inside if it's on the correct side of all 4 edges
    const d1 = this.crossProductSign(screenX, screenY, top.x, top.y, right.x, right.y);
    const d2 = this.crossProductSign(screenX, screenY, right.x, right.y, bottom.x, bottom.y);
    const d3 = this.crossProductSign(screenX, screenY, bottom.x, bottom.y, left.x, left.y);
    const d4 = this.crossProductSign(screenX, screenY, left.x, left.y, top.x, top.y);

    const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0) || (d4 < 0);
    const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0) || (d4 > 0);

    // Point is inside if all signs are the same (all positive or all negative)
    return !(hasNeg && hasPos);
  }

  /**
   * Calculate the cross product sign for point-in-polygon testing
   */
  private crossProductSign(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    return (px - x2) * (y1 - y2) - (x1 - x2) * (py - y2);
  }

  /**
   * Check for trigger tile collisions at a position
   * Call this when an entity moves to detect collision events
   * @returns Array of trigger tile positions the entity is on
   */
  public checkTriggers(worldX: number, worldY: number, entityId: string): void {
    if (!this.tilemap) return;

    const tileX = Math.floor(worldX);
    const tileY = Math.floor(worldY);
    const tileKey = `${entityId}:${tileX},${tileY}`;

    // Check collision layer for trigger tiles
    if (this.collisionData.length > 0) {
      const index = tileY * this.tilemap.width + tileX;
      const tileValue = this.collisionData[index];

      if (tileValue === TilemapManager.COLLISION_TRIGGER) {
        // Entity entered a trigger tile
        if (!this.activeTriggerTiles.has(tileKey)) {
          this.activeTriggerTiles.add(tileKey);
          EventBus.emitEvent('tile:trigger:enter', {
            entityId,
            tileX,
            tileY,
            tileType: tileValue,
          });
          if (this.debugCollision) {
            console.log(`TRIGGER ENTER: entity ${entityId} at tile (${tileX},${tileY})`);
          }
        }
      } else {
        // Entity left a trigger tile
        if (this.activeTriggerTiles.has(tileKey)) {
          this.activeTriggerTiles.delete(tileKey);
          EventBus.emitEvent('tile:trigger:exit', {
            entityId,
            tileX,
            tileY,
          });
          if (this.debugCollision) {
            console.log(`TRIGGER EXIT: entity ${entityId} left tile (${tileX},${tileY})`);
          }
        }
      }
    }

    // Also check ground/paths layers for trigger tiles
    for (const [layerName, layer] of this.layers) {
      if (layerName === 'collision') continue;

      const tile = layer.getTileAt(tileX, tileY);
      if (tile && tile.index === TilemapManager.COLLISION_TRIGGER) {
        const layerTileKey = `${entityId}:${layerName}:${tileX},${tileY}`;
        if (!this.activeTriggerTiles.has(layerTileKey)) {
          this.activeTriggerTiles.add(layerTileKey);
          EventBus.emitEvent('tile:trigger:enter', {
            entityId,
            tileX,
            tileY,
            tileType: tile.index,
            layer: layerName,
          });
        }
      }
    }
  }

  /**
   * Clear trigger state for an entity (call when entity is destroyed)
   */
  public clearEntityTriggers(entityId: string): void {
    for (const key of this.activeTriggerTiles) {
      if (key.startsWith(`${entityId}:`)) {
        this.activeTriggerTiles.delete(key);
      }
    }
  }

  /**
   * Get all spawn points
   */
  public getSpawnPoints(): SpawnPoint[] {
    return this.spawnPoints;
  }

  /**
   * Get spawn point by name
   */
  public getSpawnPoint(name: string): SpawnPoint | undefined {
    return this.spawnPoints.find((sp) => sp.name === name);
  }

  /**
   * Get spawn points by type
   */
  public getSpawnPointsByType(type: string): SpawnPoint[] {
    return this.spawnPoints.filter((sp) => sp.type === type);
  }

  /**
   * Get all trigger zones
   */
  public getTriggerZones(): TriggerZone[] {
    return this.triggerZones;
  }

  /**
   * Get the player spawn point
   */
  public getPlayerSpawn(): SpawnPoint | undefined {
    return this.spawnPoints.find(
      (sp) => sp.type === 'player_spawn' || sp.name === 'player_spawn' || sp.name === 'player'
    );
  }

  /**
   * Get a specific layer
   */
  public getLayer(name: string): Phaser.Tilemaps.TilemapLayer | undefined {
    return this.layers.get(name);
  }

  /**
   * Get the collision layer
   */
  public getCollisionLayer(): Phaser.Tilemaps.TilemapLayer | null {
    return this.collisionLayer;
  }

  /**
   * Get the ground layer (used for coordinate conversion)
   */
  public getGroundLayer(): Phaser.Tilemaps.TilemapLayer | undefined {
    return this.layers.get('ground');
  }

  /**
   * Get the tilemap
   */
  public getTilemap(): Phaser.Tilemaps.Tilemap | null {
    return this.tilemap;
  }

  /**
   * Get map dimensions in tiles
   */
  public getMapSize(): { width: number; height: number } {
    if (!this.tilemap) return { width: 0, height: 0 };
    return { width: this.tilemap.width, height: this.tilemap.height };
  }

  /**
   * Get the map offset
   */
  public getOffset(): { x: number; y: number } {
    return { x: this.offsetX, y: this.offsetY };
  }

  /**
   * Set custom offset
   */
  public setOffset(x: number, y: number): void {
    this.offsetX = x;
    this.offsetY = y;

    // Update layer positions
    for (const layer of this.layers.values()) {
      layer.setPosition(x, y);
    }
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    for (const layer of this.layers.values()) {
      layer.destroy();
    }
    this.layers.clear();

    if (this.tilemap) {
      this.tilemap.destroy();
      this.tilemap = null;
    }

    this.collisionLayer = null;
    this.spawnPoints = [];
    this.triggerZones = [];
    this.collisionData = [];
    this.activeTriggerTiles.clear();
  }
}
